import React, { Children, useEffect, useState } from "react";

import { useWindowSize } from "@assets/hooks";
import { Size } from "@assets/hooks/useWindowSize";
import { classNames } from "@assets/utilities/classNameUtilities";

type InputGroupProps = {
  /** The elements in the group. */
  children: JSX.Element[];
  /** Extend the style applied to the outer container. */
  className?: string;
  /** Extend the style applied to the divider between children elements. */
  dividerClassName?: string;
  /** The text to appear between the children elements. */
  dividerContent?: string;
  /** The gap between the elements. */
  gap?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  /** If true, the children will be returned as is, without the divider. */
  hideDividerOnMobile?: boolean;
};

const gaps = {
  1: "min-w-1",
  2: "min-w-2",
  3: "min-w-3",
  4: "min-w-4",
  5: "min-w-5",
  6: "min-w-6",
  7: "min-w-7",
  8: "min-w-8",
  9: "min-w-9",
  10: "min-w-10",
  11: "min-w-11",
};

const InputGroup: React.FC<InputGroupProps> = ({
  children,
  className,
  dividerClassName,
  dividerContent = "x",
  gap,
  hideDividerOnMobile,
}: InputGroupProps) => {
  const windowSize: Size = useWindowSize();
  const [showDivider, setShowDivider] = useState(true);

  useEffect(() => {
    if (hideDividerOnMobile) {
      const screenPoint = 768;
      setShowDivider(!!(windowSize.width && windowSize.width > screenPoint));
    }
  }, [windowSize]);

  const childrenArray = Children.toArray(children);
  return showDivider ? (
    <div className={classNames(className, "md:flex flex-row")}>
      {Children.map(childrenArray, (child, index) => {
        const isLast = index === children.length - 1;
        if (!isLast) {
          return (
            <>
              {child}
              <div
                className={classNames(
                  dividerClassName,
                  gap ? gaps[gap] : "",
                  "h-[2.375rem] text-center text-neutral-700 pt-9"
                )}
              >
                {dividerContent}
              </div>
            </>
          );
        } else {
          return child;
        }
      })}
    </div>
  ) : (
    children
  );
};

export default InputGroup;
